import apiService from '@/core/common/services/api.service';
import * as odataService from '@/core/common/services/odata.service';
import { triggerBrowserDownload } from '@/core/common/helpers/datei.utils';
import { getDokumentTypKeyFromApiValue, DOKUMENT_TYPEN } from '@/core/vorgaenge/helpers/teilnehmer-felder';

// Aktualisiert einzelne Passdaten-Felder eines Teilnehmers
// leere Werte bedeuten im Core "unverändert", geleerte Felder werden deshalb in zuLeerendeFelder übergeben
export async function updateTeilnehmer(vorgangId, teilnehmerNummer, changes) {
  const zuLeerendeFelder = Object.keys(changes).filter(key => changes[key] == null || changes[key] === '');
  const werte = Object.fromEntries(
    Object.entries(changes).filter(([key]) => !zuLeerendeFelder.includes(key))
  );
  return apiService.patch(`/Vorgang/${vorgangId}/teilnehmer/${teilnehmerNummer}/passdaten`, {
    ...werte,
    zuLeerendeFelder,
  });
}

// Lädt ein Dokument für einen Teilnehmer hoch;
export async function uploadTeilnehmerDokument(vorgangId, teilnehmerNummer, dokumentTyp, file) {
  const formData = new FormData();
  formData.append('Datei', file);
  formData.append('Type', getDokumentTypApiValue(dokumentTyp));
  const response = await apiService.post(
    `/Vorgang/${vorgangId}/teilnehmer/${teilnehmerNummer}/dokumente`,
    formData
  );
  return response.data?.result?.value ?? {};
}

function fetchTeilnehmerDokument(dokument) {
  return apiService.get(
    `/Vorgang/${dokument.vorgangId}/teilnehmer/${dokument.teilnehmerNummer}/dokumente/${dokument.id}/download`,
    '',
    { responseType: 'blob' }
  );
}

// Lädt die Datei als Blob vom Server und stößt anschließend den Browser-Download an
export async function downloadTeilnehmerDokument(dokument) {
  const response = await fetchTeilnehmerDokument(dokument);
  triggerBrowserDownload(new Blob([response.data]), dokument.dateiname);
}

const VORSCHAU_MIME_TYPES = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

// Zeigt die Datei im übergebenen Tab an, der MIME-Typ ist nötig, damit der Browser sie nicht herunterlädt
export async function openTeilnehmerDokumentInTab(dokument, tab) {
  const response = await fetchTeilnehmerDokument(dokument);
  const extension = (dokument.dateiname || '').split('.').pop().toLowerCase();
  const type = VORSCHAU_MIME_TYPES[extension] || response.headers?.['content-type'];
  const url = window.URL.createObjectURL(new Blob([response.data], { type }));
  tab.location.href = url;
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export const MALWARE_SCAN_STATE = {
  PENDING: 'Pending',
  NO_THREATS_FOUND: 'NoThreatsFound',
};

// Holt den aktuellen Virenscan-Status eines Dokuments frisch vom Server
export async function getDokumentMalwareScanState(dokument) {
  const response = await odataService.getVorgang({
    filter: { id: dokument.vorgangId },
    expand: {
      teilnehmers: {
        filter: { nummer: dokument.teilnehmerNummer, deletedAt: null },
        expand: { dokumente: {} },
      },
    },
  });
  const teilnehmer = response.data?.[0]?.teilnehmers?.[0];
  return (teilnehmer?.dokumente || []).find(d => d.id === dokument.id)?.malwareScanState ?? null;
}

export async function deleteTeilnehmerDokument(dokument) {
  return apiService.delete(
    `/Vorgang/${dokument.vorgangId}/teilnehmer/${dokument.teilnehmerNummer}/dokumente/${dokument.id}`
  );
}

// Lädt die Änderungshistorie der Passdaten eines Teilnehmers für die Verlaufsansicht
export async function getTeilnehmerHistory(vorgangId, teilnehmerNummer) {
  const response = await apiService.get(
    `/history/Vorgang/${vorgangId}/Teilnehmer/${teilnehmerNummer}/Passdaten`
  );
  return response.data?.result ?? {};
}

function getDokumentTypApiValue(dokumentTyp) {
  return DOKUMENT_TYPEN.find(typ => typ.key === dokumentTyp)?.apiValue || dokumentTyp;
}

export function normalizeDokument(dokument, vorgangId, teilnehmerNummer) {
  return {
    id: dokument.id,
    vorgangId,
    teilnehmerNummer,
    dokumentTyp: getDokumentTypKeyFromApiValue(dokument.type),
    dateiname: dokument.dateiname,
    groesse: dokument.groesse,
    // Der Scan-Status
    malwareScanState: dokument.malwareScanState,
  };
}

// Gruppiert die Dokumente je Vorgangs-ID und darunter je Teilnehmernummer
export function groupDokumenteByVorgang(vorgaenge) {
  const map = {};
  for (const vorgang of vorgaenge || []) {
    map[vorgang.id] = (vorgang.teilnehmer || []).reduce((acc, teilnehmer) => {
      // Soft-gelöschte Dokumente werden ausgefiltert, sonst schlagen Download und Löschen fehl
      acc[teilnehmer.nummer] = (teilnehmer.dokumente || [])
        .filter(dokument => !dokument.isDeleted && dokument.deletedAt == null)
        .map(dokument => normalizeDokument(dokument, vorgang.id, teilnehmer.nummer));
      return acc;
    }, {});
  }
  return map;
}
